import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ContentPage, ContentSection } from '@/components/marketing/content-page';
import { ContentFaq } from '@/components/marketing/content-faq';
import {
  setDocumentMeta, setCanonical, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema,
} from '@/lib/seo';

const FAQS = [
  {
    q: 'What is the best Freshdesk alternative?',
    a: 'The best Freshdesk alternative depends on why you are switching. For pricing without seat fees and AI ticket structuring included, Kora (pay-as-you-go credits: $1 = 10 credits, 1 credit per ticket, 1,100 free on signup) is the strongest option. For email-first simplicity, Help Scout. For the Zoho ecosystem, Zoho Desk. For enterprise omnichannel, Zendesk. Teams most often switch from Freshdesk because of per-agent costs, AI add-on fees, and per-seat limits.',
  },
  {
    q: 'Why do teams switch from Freshdesk?',
    a: 'Three recurring reasons: cost scaling (Freshdesk charges per agent per month, so a 10-agent team on Growth pays $190/month and a 25-agent team pays $475/month), AI pricing (Freddy AI capabilities are tied to higher tiers and add-ons, while Kora includes AI ticket structuring with no extra fee), and per-seat limits (Kora has unlimited members and 1,100 free credits to start).',
  },
  {
    q: 'Is there a free Freshdesk alternative?',
    a: 'Kora has no seat fees and unlimited members: new workspaces get 1,100 free credits with no card, then a ticket costs $0.10 (1 credit). AI ticket structuring is free. Zoho Desk is free for up to 3 users. Help Scout is free for 50 contacts.',
  },
];

// Verified June 2026 from Freshdesk's official pricing page; re-verified quarterly.
const COST_TABLE = [
  { agents: 5, freshdesk: 75, kora: 30 },
  { agents: 10, freshdesk: 150, kora: 30 },
  { agents: 25, freshdesk: 375, kora: 30 },
];

export function FreshdeskAlternativePage() {
  useEffect(() => {
    const title = 'Freshdesk Alternative — Flat Pricing, AI Included | Kora';
    const description =
      'Looking for a Freshdesk alternative? Kora has no seat fees and unlimited members, charges $0.10 per ticket and starts with 1,100 free credits.';
    setDocumentMeta(title, description);
    setCanonical('/freshdesk-alternative');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/freshdesk-alternative', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app' },
        { name: 'Freshdesk Alternative', url: 'https://usekora.app/freshdesk-alternative' },
      ]),
      buildFaqPageSchema(FAQS),
    ]);
    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Alternative · Updated September 2026"
      title="Looking for a Freshdesk alternative?"
      subtitle="Kora is what teams switch to when per-agent pricing and AI add-ons stop making sense."
      ctaTitle="Switch from Freshdesk — start free"
      ctaSubtitle="Pay as you go — $1 = 10 credits, 1 credit per ticket. Start with 1,100 free credits. Import via API."
      answer={
        <p>
          Kora is a Freshdesk alternative built for teams that want AI included and pricing tied to
          actual work. Freshdesk charges per agent — Growth is $19/agent/month on annual billing, so a 10-agent team
          pays $190/month and a 25-agent team pays $475/month (Pro is $55/agent). Kora charges{' '}
          <strong>per ticket with credits — $1 buys 10 credits and a ticket costs 1</strong>, with
          AI ticket structuring unlimited and an AI Analyst included (1 credit per answer) — and it
          <strong>starts with 1,100 free credits</strong> and unlimited members (Freshdesk bills per agent). Paste a Slack thread, WhatsApp forward, or customer email and Kora's AI turns
          it into a structured, prioritized ticket in seconds.
        </p>
      }
    >
      <ContentSection title="Why teams switch from Freshdesk">
        <p>
          Freshdesk is a capable help desk — the switch reasons are almost always structural
          rather than feature gaps:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Per-agent costs scale linearly.</strong> Every new hire adds a seat fee. On
            Freshdesk Growth ($19/agent/month), headcount growth directly multiplies your bill.
          </li>
          <li>
            <strong>AI costs extra.</strong> Freshworks' AI capabilities are tied to higher tiers
            and add-ons, while Kora includes AI ticket structuring with no extra fee.
          </li>
          <li>
            <strong>Per-agent minimums.</strong> Freshdesk has no free plan listed on its pricing
            page (14-day trial, checked 2026-10-19); Kora starts with 1,100 free credits and no trial clock.
          </li>
        </ul>
      </ContentSection>

      <ContentSection title="Cost comparison at team size">
        <p>
          Monthly cost comparison — Freshdesk Growth ($19/agent/month on annual billing, checked 2026-10-19 on freshworks.com/freshdesk/pricing) versus
          Kora (pay-as-you-go credits — $1 = 10 credits, $0.10 per ticket; the Kora column assumes 300 tickets a month and does not change with team size):
        </p>
        <div className="rounded-xl border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Team size</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Freshdesk Growth</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Kora</th>
              </tr>
            </thead>
            <tbody>
              {COST_TABLE.map((row) => (
                <tr key={row.agents} className="border-b border-zinc-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-zinc-900 ">{row.agents} agents</td>
                  <td className="px-4 py-3 text-zinc-500 ">${row.freshdesk}/mo</td>
                  <td className="px-4 py-3 font-medium text-green-600 ">${row.kora}/mo</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-zinc-400 ">
          Freshdesk pricing checked 2026-10-19 on the official pricing page; re-verified
          quarterly. Kora has no seat fees; members are unlimited and the cost depends only on tickets created.
        </p>
      </ContentSection>

      <ContentSection title="What you keep">
        <p>
          Everything a Freshdesk workflow depends on: categories, priorities, SLAs, a knowledge
          base workflow, a public API (20+ endpoints; 1 credit per 400 responses),
          and customer-facing ticket views. The difference is that messy intake — Slack threads,
          WhatsApp forwards, forwarded emails — becomes structured tickets automatically instead
          of manual triage work.
        </p>
      </ContentSection>

      <ContentSection title="The detailed comparison">
        <p>
          For a feature-by-feature breakdown including the full pricing matrix and where each
          tool wins, see the{' '}
          <Link to="/compare/kora-vs-freshdesk" className="underline hover:text-zinc-900 font-medium">
            Kora vs Freshdesk
          </Link>{' '}
          comparison. Other teams in this situation also compare{' '}
          <Link to="/compare/kora-vs-zoho-desk" className="underline hover:text-zinc-900 ">Zoho Desk</Link>,{' '}
          <Link to="/compare/kora-vs-help-scout" className="underline hover:text-zinc-900 ">Help Scout</Link>, and{' '}
          <Link to="/compare/kora-vs-zendesk" className="underline hover:text-zinc-900 ">Zendesk</Link>.
        </p>
      </ContentSection>

      <ContentFaq faqs={FAQS} subtitle="Questions teams ask when leaving Freshdesk." />
    </ContentPage>
  );
}
